/**
 * Admin Config - Panel de Configuración y Gestión General del Sistema
 * 
 * Gestión centralizada de usuarios del sistema (independiente de productos EPL/VRT)
 * Funcionalidades:
 * - Gestión de usuarios (Crear, Editar, Eliminar)
 * - Planes de suscripción (Free, Pro1, Pro2)
 * - Resetear contraseñas
 * - Control de acceso general
 * 
 * Nota: EPL/VRT Admin solo asignan productos específicos a usuarios existentes
 */

import React, { useState, useEffect } from 'react';
import { 
  Search, 
  UserPlus, 
  Users,
  Edit, 
  Trash2,
  Key,
  CheckCircle,
  XCircle,
  Loader,
  ArrowUpDown,
  BarChart3,
  Crown,
  Zap,
  Sparkles,
  Tag,
  Filter
} from 'lucide-react';
import apiClient from '../services/apiClient';
import AnalyticsV4OverviewPanel from '../components/AnalyticsV4OverviewPanel';

const AdminConfig = () => {
  const [activeSection, setActiveSection] = useState('users');
  
  // VRT Asignaciones
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [selectedUserForVRT, setSelectedUserForVRT] = useState(null);
  const [showVRTAssignModal, setShowVRTAssignModal] = useState(false);
  const [savingVRTAssignment, setSavingVRTAssignment] = useState(false);
  const [showOnlyAssigned, setShowOnlyAssigned] = useState(true); // Filtro por defecto: solo con agentes
  const [showUserSelectModal, setShowUserSelectModal] = useState(false); // Modal para seleccionar usuario sin agentes
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState(''); // Filtro por etiqueta
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [sortBy, setSortBy] = useState('fechaRegistro');
  const [sortOrder, setSortOrder] = useState('desc');

  // Estados para el formulario de crear/editar
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    role: 'student',
    plan: 'free',
    estado: 'activo',
    tags: [] // Array de strings
  });
  const [currentTagInput, setCurrentTagInput] = useState(''); // Input temporal para nuevas etiquetas

  // Estados para resetear contraseña
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    fetchUsers();
    if (activeSection === 'vrt_assignments') {
      fetchAgents();
      // Resetear filtros al entrar al tab VRT
      setSearchTerm('');
      setSelectedTag('');
      setShowOnlyAssigned(true); // Por defecto: solo usuarios con agentes
    }
  }, [activeSection]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/users');
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      alert('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      setLoadingAgents(true);
      const response = await apiClient.get('/vertex-admin/agents');
      setAgents(response.data.agents || []);
    } catch (error) {
      console.error('Error al cargar agentes:', error);
      alert('Error al cargar agentes VRT');
    } finally {
      setLoadingAgents(false);
    }
  };

  const handleVRTAssign = (user) => {
    setSelectedUserForVRT(user);
    setShowVRTAssignModal(true);
  };

  const handleSaveVRTAssignment = async (selectedAgents) => {
    setSavingVRTAssignment(true);
    try {
      await apiClient.put(`/vertex-admin/users/${selectedUserForVRT._id}/agents`, {
        assignedAgents: selectedAgents
      });
      setUsers(prev => prev.map(u => 
        u._id === selectedUserForVRT._id 
          ? { ...u, assignedAgents: selectedAgents }
          : u
      ));
      alert(`✅ Agentes asignados a ${selectedUserForVRT.nombre} correctamente.`);
      setShowVRTAssignModal(false);
    } catch (error) {
      console.error('Error saving assignment:', error);
      alert('No se pudieron asignar los agentes. Intenta nuevamente.');
    } finally {
      setSavingVRTAssignment(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const filteredAndSortedUsers = users
    .filter(user => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = user.nombre?.toLowerCase().includes(searchLower) ||
             user.email?.toLowerCase().includes(searchLower);
      
      const matchesTag = selectedTag ? user.tags?.includes(selectedTag) : true;
      
      return matchesSearch && matchesTag;
    })
    .sort((a, b) => {
      let aValue, bValue;
      
      if (sortBy === 'fechaRegistro') {
        aValue = new Date(a.fechaRegistro || 0);
        bValue = new Date(b.fechaRegistro || 0);
      } else if (sortBy === 'nombre') {
        aValue = a.nombre?.toLowerCase() || '';
        bValue = b.nombre?.toLowerCase() || '';
      } else if (sortBy === 'email') {
        aValue = a.email?.toLowerCase() || '';
        bValue = b.email?.toLowerCase() || '';
      } else if (sortBy === 'role') {
        aValue = a.role || '';
        bValue = b.role || '';
      } else if (sortBy === 'plan') {
        const planOrder = { free: 0, pro1: 1, pro2: 2 };
        aValue = planOrder[a.plan] || 0;
        bValue = planOrder[b.plan] || 0;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  // Obtener todas las etiquetas únicas para el filtro
  const allTags = Array.from(new Set(users.flatMap(u => u.tags || []))).sort();

  const handleAddTag = (e) => {
    // Si es evento de teclado, solo actuar en Enter
    if (e.type === 'keydown' && e.key !== 'Enter') return;
    
    e.preventDefault(); // Prevenir submit del form
    
    const tag = currentTagInput.trim();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), tag]
      }));
      setCurrentTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleCreateUser = () => {
    setFormData({
      nombre: '',
      email: '',
      role: 'student',
      plan: 'free',
      estado: 'activo',
      tags: []
    });
    setCurrentTagInput('');
    setShowCreateModal(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setFormData({
      nombre: user.nombre,
      email: user.email,
      role: user.role,
      plan: user.plan || 'free',
      estado: user.estado,
      tags: user.tags || []
    });
    setCurrentTagInput('');
    setShowEditModal(true);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setShowResetPasswordModal(true);
  };

  const submitCreateUser = async () => {
    try {
      if (!formData.nombre || !formData.email) {
        alert('Nombre y email son requeridos');
        return;
      }

      // Si hay texto pendiente en el input de tags, agregarlo antes de enviar
      let finalTags = [...(formData.tags || [])];
      const pendingTag = currentTagInput.trim();
      
      if (pendingTag && !finalTags.includes(pendingTag)) {
        finalTags.push(pendingTag);
      }

      const dataToSend = {
        ...formData,
        tags: finalTags
      };

      await apiClient.post('/admin/users', dataToSend);
      alert('Usuario creado exitosamente');
      setShowCreateModal(false);
      fetchUsers();
    } catch (error) {
      console.error('Error al crear usuario:', error);
      alert(error.response?.data?.message || 'Error al crear usuario');
    }
  };

  const submitEditUser = async () => {
    try {
      // Si hay texto pendiente en el input de tags, agregarlo antes de enviar
      let finalTags = [...(formData.tags || [])];
      const pendingTag = currentTagInput.trim();
      
      if (pendingTag && !finalTags.includes(pendingTag)) {
        finalTags.push(pendingTag);
        console.log('Agregando etiqueta pendiente automáticamente:', pendingTag);
      }

      const dataToSend = {
        ...formData,
        tags: finalTags
      };

      console.log('Enviando actualización de usuario:', dataToSend);
      await apiClient.put(`/admin/users/${selectedUser._id}`, dataToSend);
      alert('Usuario actualizado exitosamente');
      setShowEditModal(false);
      fetchUsers();
    } catch (error) {
      console.error('Error al actualizar usuario:', error);
      alert('Error al actualizar usuario');
    }
  };

  const submitDeleteUser = async () => {
    try {
      await apiClient.delete(`/admin/users/${selectedUser._id}`);
      alert('Usuario eliminado exitosamente');
      setShowDeleteModal(false);
      fetchUsers();
    } catch (error) {
      console.error('Error al eliminar usuario:', error);
      alert('Error al eliminar usuario');
    }
  };

  const submitResetPassword = async () => {
    try {
      if (newPassword !== confirmPassword) {
        alert('Las contraseñas no coinciden');
        return;
      }

      if (newPassword.length < 6) {
        alert('La contraseña debe tener al menos 6 caracteres');
        return;
      }

      const response = await apiClient.post(`/admin/users/${selectedUser._id}/reset-password`, {
        newPassword
      });
      
      alert(`Contraseña actualizada exitosamente\n\nUsuario: ${response.data.email}\nNueva contraseña: ${response.data.newPassword}`);
      setShowResetPasswordModal(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error al resetear contraseña:', error);
      alert(error.response?.data?.message || 'Error al resetear contraseña');
    }
  };

  const getPlanBadge = (plan) => {
    const plans = {
      free: { label: 'Free', icon: Sparkles, color: 'bg-gray-100 text-gray-700' },
      pro1: { label: 'Pro 1', icon: Zap, color: 'bg-blue-100 text-blue-700' },
      pro2: { label: 'Pro 2', icon: Crown, color: 'bg-purple-100 text-purple-700' }
    };
    
    const config = plans[plan] || plans.free;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${config.color}`}>
        <Icon size={12} />
        {config.label}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const roles = {
      admin: { label: 'Admin', color: 'bg-red-100 text-red-700' },
      instructor: { label: 'Instructor', color: 'bg-orange-100 text-orange-700' },
      student: { label: 'Estudiante', color: 'bg-green-100 text-green-700' }
    };
    
    const config = roles[role] || roles.student;
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${config.color}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="animate-spin text-blue-600" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-50 p-3 md:p-5">
      {/* Header compacto */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h1 className="text-base md:text-xl font-bold text-gray-900 leading-tight">Configuración Admin</h1>
          <p className="text-xs text-gray-500">Gestión centralizada de usuarios y planes</p>
        </div>
        <span className="text-xs text-gray-400 font-mono bg-gray-100 px-2 py-1 rounded">
          {users.length} usuarios
        </span>
      </div>

      {/* Tabs — compactos, scroll horizontal en móvil */}
      <div className="mb-4 -mx-3 md:mx-0">
        <div className="px-3 md:px-0 overflow-x-auto">
          <div className="inline-flex min-w-max rounded-lg bg-white p-0.5 shadow-sm border border-gray-200">
            <button
              type="button"
              onClick={() => setActiveSection('users')}
              className={
                activeSection === 'users'
                  ? 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-gray-900 text-white text-xs font-medium'
                  : 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-gray-600 text-xs font-medium hover:bg-gray-50'
              }
            >
              <Users size={13} />
              Usuarios
            </button>
            <button
              type="button"
              onClick={() => setActiveSection('vrt_assignments')}
              className={
                activeSection === 'vrt_assignments'
                  ? 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-gray-900 text-white text-xs font-medium'
                  : 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-gray-600 text-xs font-medium hover:bg-gray-50'
              }
            >
              <UserPlus size={13} />
              VRT - Asignaciones
            </button>
            <button
              type="button"
              onClick={() => setActiveSection('vrt_analytics')}
              className={
                activeSection === 'vrt_analytics'
                  ? 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-gray-900 text-white text-xs font-medium'
                  : 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-gray-600 text-xs font-medium hover:bg-gray-50'
              }
            >
              <BarChart3 size={13} />
              Analytics
            </button>
          </div>
        </div>
      </div>

      {activeSection === 'users' ? (
        <>
          {/* Barra de acciones */}
          <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              {/* Búsqueda */}
              <div className="relative flex-1 w-full md:max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Buscar por nombre o email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Filtro por Etiqueta */}
              <div className="relative w-full md:w-48">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <select
                  value={selectedTag}
                  onChange={(e) => setSelectedTag(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                >
                  <option value="">Todas las etiquetas</option>
                  {allTags.map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </select>
              </div>

              {/* Botón crear usuario */}
              <button
                onClick={handleCreateUser}
                className="w-full md:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
              >
                <UserPlus size={16} />
                Crear Usuario
              </button>
            </div>
          </div>

          {/* Tabla de usuarios (Desktop) */}
          <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto w-full">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th 
                  onClick={() => handleSort('nombre')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center gap-2">
                    Nombre
                    <ArrowUpDown size={14} />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('email')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center gap-2">
                    Email
                    <ArrowUpDown size={14} />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Etiquetas
                </th>
                <th 
                  onClick={() => handleSort('role')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center gap-2">
                    Rol
                    <ArrowUpDown size={14} />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('plan')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center gap-2">
                    Plan
                    <ArrowUpDown size={14} />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Verificado
                </th>
                <th 
                  onClick={() => handleSort('fechaRegistro')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center gap-2">
                    Registro
                    <ArrowUpDown size={14} />
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAndSortedUsers.map((user) => (
                <tr key={user._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">{user.nombre}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">{user.email}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1 max-w-xs">
                      {user.tags && user.tags.length > 0 ? (
                        user.tags.map((tag, idx) => (
                          <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                            {tag}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-gray-400 italic">Sin etiquetas</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getRoleBadge(user.role)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getPlanBadge(user.plan || 'free')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.estado === 'activo' ? (
                      <span className="flex items-center gap-1 text-green-600">
                        <CheckCircle size={16} />
                        <span className="text-sm">Activo</span>
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-red-600">
                        <XCircle size={16} />
                        <span className="text-sm">Inactivo</span>
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.verificado ? (
                      <span className="flex items-center gap-1 text-green-600">
                        <CheckCircle size={16} />
                        <span className="text-sm">Verificado</span>
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-amber-600">
                        <XCircle size={16} />
                        <span className="text-sm">Pendiente</span>
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {new Date(user.fechaRegistro).toLocaleDateString('es-ES')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEditUser(user)}
                        className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Editar usuario"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleResetPassword(user)}
                        className="text-orange-600 hover:text-orange-800 p-2 hover:bg-orange-50 rounded-lg transition-colors"
                        title="Resetear contraseña"
                      >
                        <Key size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user)}
                        className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-lg transition-colors"
                        title="Eliminar usuario"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredAndSortedUsers.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No se encontraron usuarios
          </div>
        )}
      </div>

      {/* Vista Móvil (Cards) */}
      <div className="md:hidden space-y-4">
        {filteredAndSortedUsers.map((user) => (
          <div key={user._id} className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="font-semibold text-gray-900">{user.nombre}</h3>
                <p className="text-sm text-gray-500">{user.email}</p>
              </div>
              {getRoleBadge(user.role)}
            </div>

            <div className="flex flex-wrap gap-2 mb-3">
              {user.tags && user.tags.length > 0 ? (
                user.tags.map((tag, idx) => (
                  <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                    {tag}
                  </span>
                ))
              ) : (
                <span className="text-xs text-gray-400 italic">Sin etiquetas</span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2 text-sm mb-4">
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Plan:</span>
                {getPlanBadge(user.plan || 'free')}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Estado:</span>
                {user.estado === 'activo' ? (
                  <span className="text-green-600 flex items-center gap-1"><CheckCircle size={14} /> Activo</span>
                ) : (
                  <span className="text-red-600 flex items-center gap-1"><XCircle size={14} /> Inactivo</span>
                )}
              </div>
              <div className="col-span-2 text-gray-500 text-xs">
                Registrado: {new Date(user.fechaRegistro).toLocaleDateString('es-ES')}
              </div>
            </div>

            <div className="flex gap-2 pt-3 border-t border-gray-100">
              <button
                onClick={() => handleEditUser(user)}
                className="flex-1 flex items-center justify-center gap-1 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100"
              >
                <Edit size={16} /> Editar
              </button>
              <button
                onClick={() => handleResetPassword(user)}
                className="flex-1 flex items-center justify-center gap-1 py-2 bg-orange-50 text-orange-700 rounded-lg text-sm font-medium hover:bg-orange-100"
              >
                <Key size={16} /> Pass
              </button>
              <button
                onClick={() => handleDeleteUser(user)}
                className="flex-1 flex items-center justify-center gap-1 py-2 bg-red-50 text-red-700 rounded-lg text-sm font-medium hover:bg-red-100"
              >
                <Trash2 size={16} /> Borrar
              </button>
            </div>
          </div>
        ))}
        
        {filteredAndSortedUsers.length === 0 && (
          <div className="text-center py-12 text-gray-500 bg-white rounded-xl">
            No se encontraron usuarios
          </div>
        )}
      </div>

      {/* Modal Crear Usuario */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl max-w-md w-full max-h-[92vh] overflow-y-auto p-5 sm:p-6">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">Crear Usuario</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Nombre completo"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="email@ejemplo.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="student">Estudiante</option>
                  <option value="instructor">Instructor</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
                <select
                  value={formData.plan}
                  onChange={(e) => setFormData({...formData, plan: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="free">Free</option>
                  <option value="pro1">Pro 1</option>
                  <option value="pro2">Pro 2</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                <select
                  value={formData.estado}
                  onChange={(e) => setFormData({...formData, estado: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="activo">Activo</option>
                  <option value="inactivo">Inactivo</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Etiquetas</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    list="available-tags-create"
                    value={currentTagInput}
                    onChange={(e) => setCurrentTagInput(e.target.value)}
                    onKeyDown={handleAddTag}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Escribe o selecciona..."
                  />
                  <datalist id="available-tags-create">
                    {allTags.map(tag => (
                      <option key={tag} value={tag} />
                    ))}
                  </datalist>
                  <button
                    type="button"
                    onClick={handleAddTag}
                    className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                    title="Agregar etiqueta"
                  >
                    <Tag size={18} />
                  </button>
                </div>

                {/* Sugerencias de etiquetas existentes */}
                {allTags.filter(t => !formData.tags?.includes(t)).length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-400 mb-1">Sugerencias (clic para agregar):</p>
                    <div className="flex flex-wrap gap-1">
                      {allTags
                        .filter(tag => !formData.tags?.includes(tag))
                        .filter(tag => !currentTagInput || tag.toLowerCase().includes(currentTagInput.toLowerCase()))
                        .slice(0, 10)
                        .map(tag => (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => {
                               setFormData({
                                  ...formData,
                                  tags: [...(formData.tags || []), tag]
                                });
                                setCurrentTagInput('');
                            }}
                            className="px-2 py-1 text-xs bg-gray-50 text-gray-600 border border-gray-200 rounded hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-colors"
                          >
                            + {tag}
                          </button>
                        ))}
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  {formData.tags && formData.tags.map((tag, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="text-indigo-600 hover:text-indigo-900 font-bold"
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">Usa etiquetas para agrupar usuarios (ej. "Clase A", "Sección 1")</p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={submitCreateUser}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Crear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar Usuario */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl max-w-md w-full max-h-[92vh] overflow-y-auto p-5 sm:p-6">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">Editar Usuario</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="student">Estudiante</option>
                  <option value="instructor">Instructor</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
                <select
                  value={formData.plan}
                  onChange={(e) => setFormData({...formData, plan: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="free">Free</option>
                  <option value="pro1">Pro 1</option>
                  <option value="pro2">Pro 2</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                <select
                  value={formData.estado}
                  onChange={(e) => setFormData({...formData, estado: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="activo">Activo</option>
                  <option value="inactivo">Inactivo</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">Control administrativo para activar/desactivar acceso</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Etiquetas</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    list="available-tags-edit"
                    value={currentTagInput}
                    onChange={(e) => setCurrentTagInput(e.target.value)}
                    onKeyDown={handleAddTag}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Escribe o selecciona..."
                  />
                  <datalist id="available-tags-edit">
                    {allTags.map(tag => (
                      <option key={tag} value={tag} />
                    ))}
                  </datalist>
                  <button
                    type="button"
                    onClick={handleAddTag}
                    className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                    title="Agregar etiqueta"
                  >
                    <Tag size={18} />
                  </button>
                </div>

                {/* Sugerencias de etiquetas existentes */}
                {allTags.filter(t => !formData.tags?.includes(t)).length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-400 mb-1">Sugerencias (clic para agregar):</p>
                    <div className="flex flex-wrap gap-1">
                      {allTags
                        .filter(tag => !formData.tags?.includes(tag))
                        .filter(tag => !currentTagInput || tag.toLowerCase().includes(currentTagInput.toLowerCase()))
                        .slice(0, 10)
                        .map(tag => (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => {
                               setFormData({
                                  ...formData,
                                  tags: [...(formData.tags || []), tag]
                                });
                                setCurrentTagInput('');
                            }}
                            className="px-2 py-1 text-xs bg-gray-50 text-gray-600 border border-gray-200 rounded hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-colors"
                          >
                            + {tag}
                          </button>
                        ))}
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  {formData.tags && formData.tags.map((tag, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="text-indigo-600 hover:text-indigo-900 font-bold"
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">Usa etiquetas para agrupar usuarios (ej. "Clase A", "Sección 1")</p>
              </div>

              {/* Campo Verificado - Solo lectura */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Verificación de Email</label>
                <div className="flex items-center gap-2">
                  {selectedUser?.verificado ? (
                    <>
                      <CheckCircle className="text-green-600" size={16} />
                      <span className="text-sm text-green-700 font-medium">Email verificado</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="text-amber-600" size={16} />
                      <span className="text-sm text-amber-700 font-medium">Pendiente de verificación</span>
                    </>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">Solo el usuario puede verificar su email con el código enviado</p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={submitEditUser}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Resetear Contraseña */}
      {showResetPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Key className="text-orange-600" size={24} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Resetear Contraseña</h3>
                <p className="text-sm text-gray-600">{selectedUser?.nombre}</p>
              </div>
            </div>
            
            <div className="space-y-4 mt-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nueva contraseña *</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="Mínimo 6 caracteres"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar contraseña *</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="Repetir contraseña"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowResetPasswordModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={submitResetPassword}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                Actualizar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Eliminar Usuario */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <Trash2 className="text-red-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Eliminar Usuario</h3>
            </div>
            
            <p className="text-gray-600 mb-2">
              Esta acción no se puede deshacer.
            </p>
            <p className="text-gray-600 mb-4">
              ¿Estás seguro de eliminar este usuario?
            </p>
            
            <div className="bg-gray-50 p-3 rounded-lg mb-6">
              <p className="font-semibold text-gray-900">{selectedUser?.nombre}</p>
              <p className="text-sm text-gray-600">{selectedUser?.email}</p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={submitDeleteUser}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

        </>
      ) : activeSection === 'vrt_assignments' ? (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {loadingAgents ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="w-8 h-8 text-indigo-600 animate-spin" />
            </div>
          ) : (
            <>
              <div className="p-4 md:p-6 border-b border-gray-200">
                <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-1">Asignar Agentes VRT a Usuarios</h3>
                <p className="text-xs md:text-sm text-gray-600">Selecciona qué agentes de simulación puede usar cada estudiante</p>
              </div>
              
              <div className="p-4 md:p-6">
                {/* Controles de filtrado */}
                <div className="mb-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      type="text"
                      placeholder="Buscar usuario..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                  <button
                    onClick={() => setShowOnlyAssigned(!showOnlyAssigned)}
                    className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex-shrink-0 ${
                      showOnlyAssigned
                        ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Filter size={15} />
                    <span className="sm:hidden">{showOnlyAssigned ? 'Con agentes' : 'Todos'}</span>
                    <span className="hidden sm:inline">{showOnlyAssigned ? 'Solo con agentes' : 'Mostrar todos'}</span>
                  </button>
                </div>
                {/* Contador de resultados */}
                <div className="mb-3">
                  <p className="text-xs text-gray-500">
                    {(() => {
                      const usersWithAgents = filteredAndSortedUsers.filter(u => u.assignedAgents?.length > 0);
                      const displayCount = showOnlyAssigned ? usersWithAgents.length : filteredAndSortedUsers.length;
                      return (
                        <>
                          Mostrando {displayCount} usuario{displayCount !== 1 ? 's' : ''}
                          {showOnlyAssigned && ` con agentes asignados`}
                          {searchTerm && ` · Búsqueda: "${searchTerm}"`}
                          {!showOnlyAssigned && ` (${usersWithAgents.length} con agentes)`}
                        </>
                      );
                    })()}
                  </p>
                </div>

                {/* Vista móvil: cards de asignaciones VRT */}
                <div className="md:hidden space-y-3 mb-3">
                  {(() => {
                    const mobileUsers = showOnlyAssigned
                      ? filteredAndSortedUsers.filter(u => u.assignedAgents?.length > 0)
                      : filteredAndSortedUsers;
                    if (mobileUsers.length === 0) return (
                      <div className="text-center py-10 text-gray-400">
                        <Users className="mx-auto mb-2 w-10 h-10 text-gray-300" />
                        <p className="text-sm font-medium">{showOnlyAssigned ? 'Sin usuarios con agentes' : 'Sin usuarios'}</p>
                        {showOnlyAssigned && (
                          <button onClick={() => setShowUserSelectModal(true)} className="mt-2 text-xs text-indigo-600 font-medium">Asignar primer agente →</button>
                        )}
                      </div>
                    );
                    return mobileUsers.map(user => (
                      <div key={user._id} className="bg-gray-50 rounded-xl border border-gray-200 p-3">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                            {user.nombre?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-900 text-sm truncate">{user.nombre}</p>
                            <p className="text-xs text-gray-500 truncate">{user.email}</p>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 font-medium ${
                            user.assignedAgents?.length > 0 ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-400'
                          }`}>
                            {user.assignedAgents?.length > 0 ? `${user.assignedAgents.length} agente${user.assignedAgents.length !== 1 ? 's' : ''}` : 'Sin asignar'}
                          </span>
                        </div>
                        {user.assignedAgents && user.assignedAgents.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-3">
                            {user.assignedAgents.slice(0, 3).map(agentId => {
                              const agent = agents.find(a => a.agentId === agentId);
                              return (
                                <span key={agentId} className="px-2 py-0.5 bg-indigo-50 text-indigo-600 text-xs rounded border border-indigo-200">
                                  {agent?.nombreVisible || agentId.substring(0, 10) + '…'}
                                </span>
                              );
                            })}
                            {user.assignedAgents.length > 3 && (
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded">+{user.assignedAgents.length - 3}</span>
                            )}
                          </div>
                        )}
                        <button
                          onClick={() => handleVRTAssign(user)}
                          className="w-full flex items-center justify-center gap-2 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                        >
                          <UserPlus size={14} />
                          {user.assignedAgents?.length > 0 ? 'Editar agentes' : 'Asignar agentes'}
                        </button>
                      </div>
                    ));
                  })()}
                </div>

                {/* Vista desktop: tabla */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuario</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agentes Asignados</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(() => {
                        // Aplicar filtro de "solo con agentes" si está activo
                        const displayedUsers = showOnlyAssigned
                          ? filteredAndSortedUsers.filter(u => u.assignedAgents?.length > 0)
                          : filteredAndSortedUsers;

                        if (displayedUsers.length === 0) {
                          return (
                            <tr>
                              <td colSpan={4} className="px-6 py-12 text-center">
                                <div className="flex flex-col items-center gap-2">
                                  <Users className="w-12 h-12 text-gray-300" />
                                  <p className="text-gray-500 font-medium">
                                    {showOnlyAssigned
                                      ? 'No hay usuarios con agentes asignados'
                                      : 'No hay usuarios que mostrar'}
                                  </p>
                                  {searchTerm ? (
                                    <p className="text-sm text-gray-400">
                                      Intenta limpiar el filtro de búsqueda
                                    </p>
                                  ) : showOnlyAssigned ? (
                                    <button
                                      onClick={() => setShowUserSelectModal(true)}
                                      className="mt-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                                    >
                                      Asignar primer agente a un usuario →
                                    </button>
                                  ) : (
                                    <p className="text-sm text-gray-400">
                                      Crea usuarios en el tab "Usuarios del Sistema" primero
                                    </p>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        }

                        return displayedUsers.map(user => (
                        <tr key={user._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                                {user.nombre?.charAt(0)?.toUpperCase() || 'U'}
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{user.nombre}</p>
                                <p className="text-sm text-gray-500">{user.role || 'student'}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-gray-700">{user.email}</td>
                          <td className="px-6 py-4">
                            {user.assignedAgents && user.assignedAgents.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {user.assignedAgents.slice(0, 2).map(agentId => {
                                  const agent = agents.find(a => a.agentId === agentId);
                                  return (
                                    <span key={agentId} className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-full font-medium">
                                      {agent?.nombreVisible || agentId.substring(0, 12)}
                                    </span>
                                  );
                                })}
                                {user.assignedAgents.length > 2 && (
                                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                                    +{user.assignedAgents.length - 2}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">Sin asignación</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button
                              onClick={() => handleVRTAssign(user)}
                              className="inline-flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                            >
                              <UserPlus size={14} />
                              {user.assignedAgents?.length > 0 ? 'Editar agentes' : 'Asignar agentes'}
                            </button>
                          </td>
                        </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm p-4">
          <AnalyticsV4OverviewPanel />
        </div>
      )}

      {/* User Select Modal (para asignar a nuevo usuario) */}
      {showUserSelectModal && (
        <UserSelectModal
          users={users}
          onSelectUser={(user) => {
            setShowUserSelectModal(false);
            handleVRTAssign(user);
          }}
          onClose={() => setShowUserSelectModal(false)}
        />
      )}

      {/* VRT Assignment Modal */}
      {showVRTAssignModal && selectedUserForVRT && (
        <VRTAssignModal
          user={selectedUserForVRT}
          agents={agents}
          onClose={() => setShowVRTAssignModal(false)}
          onSave={handleSaveVRTAssignment}
          saving={savingVRTAssignment}
        />
      )}
    </div>
  );
};

// User Select Modal Component (para asignar agentes a nuevo usuario)
const UserSelectModal = ({ users, onSelectUser, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filtrar usuarios sin agentes asignados (prioridad) y por búsqueda
  const filteredUsers = users
    .filter(user => {
      const matchesSearch = 
        user.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    })
    .sort((a, b) => {
      // Priorizar usuarios SIN agentes asignados
      const aHasAgents = a.assignedAgents?.length > 0;
      const bHasAgents = b.assignedAgents?.length > 0;
      if (!aHasAgents && bHasAgents) return -1;
      if (aHasAgents && !bHasAgents) return 1;
      return a.nombre?.localeCompare(b.nombre);
    });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
        <div className="p-4 md:p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <div>
              <h3 className="text-base md:text-xl font-bold text-gray-900">Seleccionar Usuario</h3>
              <p className="text-xs md:text-sm text-gray-600 mt-1">
                Elige a qué usuario deseas asignarle agentes VRT
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XCircle size={24} />
            </button>
          </div>

          {/* Barra de búsqueda */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar por nombre o email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              autoFocus
            />
          </div>
        </div>

        <div className="overflow-y-auto max-h-[calc(80vh-200px)]">
          {filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Users className="w-16 h-16 text-gray-300 mb-3" />
              <p className="text-gray-500 font-medium">No se encontraron usuarios</p>
              <p className="text-sm text-gray-400 mt-1">
                {searchTerm ? 'Intenta con otro término de búsqueda' : 'Crea usuarios primero en el sistema'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredUsers.map(user => (
                <div
                  key={user._id}
                  onClick={() => onSelectUser(user)}
                  className="p-4 hover:bg-indigo-50 cursor-pointer transition-colors flex items-center justify-between"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                      {user.nombre?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{user.nombre}</p>
                      <p className="text-sm text-gray-600">{user.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                          {user.role || 'student'}
                        </span>
                        {user.assignedAgents?.length > 0 && (
                          <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded font-medium">
                            {user.assignedAgents.length} agente{user.assignedAgents.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <UserPlus className="w-5 h-5 text-indigo-600" />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <p className="text-xs text-gray-500 text-center">
            {filteredUsers.length} usuario{filteredUsers.length !== 1 ? 's' : ''} disponible{filteredUsers.length !== 1 ? 's' : ''}
            {searchTerm && ` · Búsqueda: "${searchTerm}"`}
          </p>
        </div>
      </div>
    </div>
  );
};

// VRT Assignment Modal Component
const VRTAssignModal = ({ user, agents, onClose, onSave, saving }) => {
  const [selectedAgents, setSelectedAgents] = useState(user.assignedAgents || []);

  const toggleAgent = (agentId) => {
    setSelectedAgents(prev =>
      prev.includes(agentId)
        ? prev.filter(id => id !== agentId)
        : [...prev, agentId]
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        <div className="p-4 md:p-6 border-b border-gray-200">
          <h3 className="text-base md:text-xl font-bold text-gray-900">Asignar Agentes VRT</h3>
          <p className="text-xs md:text-sm text-gray-600 mt-1">
            <span className="font-semibold">{user.nombre}</span> · <span className="text-gray-400">{user.email}</span>
          </p>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(80vh-180px)]">
          {agents.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              No hay agentes disponibles. Crea agentes primero en VRT Lab Builder.
            </p>
          ) : (
            <div className="space-y-2">
              {agents.map(agent => (
                <div
                  key={agent.agentId}
                  onClick={() => toggleAgent(agent.agentId)}
                  className="flex items-start gap-4 p-4 border-2 border-gray-200 rounded-lg hover:border-indigo-400 hover:bg-indigo-50 cursor-pointer transition-all"
                >
                  <div className={`flex items-center justify-center w-6 h-6 rounded-md border-2 flex-shrink-0 mt-0.5 transition-all ${
                    selectedAgents.includes(agent.agentId)
                      ? 'bg-indigo-600 border-indigo-600'
                      : 'bg-white border-gray-300'
                  }`}>
                    {selectedAgents.includes(agent.agentId) && (
                      <CheckCircle className="w-4 h-4 text-white" strokeWidth={3} />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{agent.nombreVisible || agent.name}</p>
                    <p className="text-sm text-gray-600 mt-1">{agent.descripcion || agent.description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 md:p-6 border-t border-gray-200 flex gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 md:flex-none px-4 md:px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={() => onSave(selectedAgents)}
            disabled={saving}
            className="flex-1 md:flex-none px-4 md:px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving && <Loader className="w-4 h-4 animate-spin" />}
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminConfig;
